/*
 * @relax Relay fragment colocation warnings are expected in this file.
 * BoardActions fragment and board node fields are used by the extracted BoardItem component.
 * This follows the same pattern as HorizontalNavBoardHeader component.
 */
import React, { useCallback, useEffect, useMemo } from 'react';
import { graphql, useRefetchableFragment } from 'react-relay';
import { useSuspenselessRefetch } from '@jira/issue__use-suspenseless-refetch/src/index.tsx';

import { useSubscribeToFavorite } from '@atlassian/jira-favourite-change-provider/src/common/utils/favorite-pub-sub/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { SCRUM } from '@atlassian/jira-common-directory-v2/src/model/constants.tsx';
import { BoardActions } from '@atlassian/jira-navigation-board-action-menu/src/ui/BoardActions.tsx';
import { useProjectsAnalyticsL2 } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l2/index.tsx';
import {
	ENTITY_ID,
	MENU_ID_BOARDS_VIEW_ALL,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { useSidebarNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { useSidebarNav4Controller } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-controller-context/index.tsx';
import { ViewAllEntityMenuItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/ui/view-all-entity-menu-item/index.tsx';
import { boardsDirectoryRoute } from '@atlassian/jira-router-routes-directories-routes/src/boardsDirectoryRoute.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	type RouterActionsType,
	usePathParam,
	useRouterActions,
} from '@atlassian/react-resource-router';
import {
	NO_OF_ADDITIONAL_BOARDS_TO_SHOW,
	NO_OF_BOARDS_TO_FETCH,
} from '../../../../common/constants.tsx';
import type { JswBoardsRefactored$key } from './__generated__/JswBoardsRefactored.graphql';
import RefetchQuery, {
	type JswBoardsRefactoredRefetchQuery,
} from './__generated__/JswBoardsRefactoredRefetchQuery.graphql';
import {
	getFavoriteBoardCount,
	convertGraphQLBoardEdges,
	getOrderedFavoriteBoardsWithOriginalTypes,
	hasFavoriteBoards,
	hasMoreFavoriteBoards,
} from './board-ordering-utils.tsx';
import messages from './messages.tsx';
import { RenderRecentBoardsSection } from './RenderRecentBoardsSection.tsx';
import { StarredSectionWithFocusRestoration } from './StarredSectionWithFocusRestoration.tsx';
import { BoardItem } from './BoardItem.tsx';

type JswBoardsRefactoredProps = {
	queryRef: JswBoardsRefactored$key;
	cloudId: string;
	entityAri: string;
	ownerAri: string;
};

export function JswBoardsRefactored({
	queryRef,
	cloudId,
	entityAri,
	ownerAri,
}: JswBoardsRefactoredProps) {
	const { sendBoardNavigationItemInsideProjectsAnalyticsEvent, sendViewAllBoardsAnalyticsEvent } =
		useProjectsAnalyticsL2();

	const [data, refetch] = useRefetchableFragment<
		JswBoardsRefactoredRefetchQuery,
		JswBoardsRefactored$key
	>(
		graphql`
			fragment JswBoardsRefactored on Query
			@argumentDefinitions(
				noOfBoardsToFetch: { type: "Int!" }
				projectKey: { type: "String!" }
				entityAri: { type: "ID!" }
				ownerAri: { type: "ID!" }
				cloudId: { type: "ID!" }
			)
			@refetchable(queryName: "JswBoardsRefactoredRefetchQuery") {
				jira @required(action: THROW) {
					jiraProjectByKey(cloudId: $cloudId, key: $projectKey) @required(action: THROW) {
						# Fragment used by BoardItem -> BoardActions
						...BoardActions
						id
						isFavourite
						projectId @required(action: THROW)
						projectKey: key @required(action: THROW)
						projectType
						boards(first: $noOfBoardsToFetch) {
							edges {
								node {
									# Used by BoardItem for BoardActions board operations
									__typename @required(action: THROW)
									id
									name
									boardId
									# Used by BoardItem for href building
									defaultNavigationItem {
										... on JiraNavigationItem {
											# Used by BoardItem for href building
											url
										}
									}
									favouriteValue {
										isFavourite
									}
								}
							}
						}
						mostRecentlyViewedBoard {
							... on JiraBoard {
								boardId
							}
						}
						softwareBoardCount @required(action: THROW)
					}
				}
				settings_navigationCustomisation(entityAri: $entityAri, ownerAri: $ownerAri) {
					sidebar {
						nodes {
							menuId
							visible
						}
					}
				}
			}
		`,
		queryRef,
	);

	const project = data.jira.jiraProjectByKey;
	const order = data.settings_navigationCustomisation?.sidebar?.nodes;

	// Current sidebar order data
	const currentSidebarOrder = useMemo(() => {
		const result =
			order
				?.map((node) => ({
					menuId: node?.menuId || '',
					visible: node?.visible || false,
				}))
				.filter((item) => item.menuId) || [];

		return result;
	}, [order]);

	const [boardsSuspenselessRefetch] = useSuspenselessRefetch(RefetchQuery, refetch, {
		fetchPolicy: 'network-only',
	});

	const onSubscribeToFavorite = useCallback(() => {
		boardsSuspenselessRefetch({
			projectKey: project?.projectKey || '',
			noOfBoardsToFetch: NO_OF_BOARDS_TO_FETCH,
			cloudId,
			entityAri,
			ownerAri,
		});
	}, [boardsSuspenselessRefetch, project?.projectKey, cloudId, entityAri, ownerAri]);

	useSubscribeToFavorite('JiraBoard', onSubscribeToFavorite);

	const { formatMessage } = useIntl();
	const { isSelectedPath } = useSidebarNav4();
	const { isControlledPath } = useSidebarNav4Controller();

	const viewAllBoardsInProjectHref = `${boardsDirectoryRoute.path}?projectLocation=${project?.projectKey}`;

	// Get favorite board count using refactored utility function
	const noOfFavouriteBoards = useMemo(() => {
		const boardEdges = convertGraphQLBoardEdges(project?.boards?.edges || []);
		return getFavoriteBoardCount(boardEdges);
	}, [project?.boards?.edges]);

	const noOfBoardsToShow = Math.min(
		noOfFavouriteBoards + NO_OF_ADDITIONAL_BOARDS_TO_SHOW,
		NO_OF_BOARDS_TO_FETCH,
	);

	const boardsToShow = useMemo(() => {
		return fg('blu-4626-sorted-project-boards-fe')
			? (project?.boards?.edges?.slice(0, noOfBoardsToShow) ?? [])
			: project?.boards?.edges;
	}, [project?.boards?.edges, noOfBoardsToShow]);

	// Apply custom ordering to favorite boards using refactored utility function
	const orderedFavoriteBoards = useMemo(() => {
		const boardEdges = project?.boards?.edges?.slice(0, noOfBoardsToShow) || [];

		const result = getOrderedFavoriteBoardsWithOriginalTypes(boardEdges, currentSidebarOrder);

		return result;
	}, [project?.boards?.edges, noOfBoardsToShow, currentSidebarOrder]);

	const showViewAllBoards = fg('blu-4626-sorted-project-boards-fe')
		? project.softwareBoardCount > noOfBoardsToShow
		: project.softwareBoardCount > NO_OF_BOARDS_TO_FETCH;

	const mostRecentlyViewedBoardId = project.mostRecentlyViewedBoard?.boardId;

	const projId = ENTITY_ID.PROJECT(project.projectKey);
	const isProjectSelected = isSelectedPath(projId);
	const isAnyBoardSelected =
		boardsToShow?.some(
			(board) =>
				board &&
				board.node &&
				board.node.boardId &&
				isSelectedPath(ENTITY_ID.BOARD(board.node.boardId.toString())),
		) ?? false;
	const isProjectScoped = isProjectSelected && !isAnyBoardSelected;
	const projectKey = project.projectKey;

	const boardIdPath = usePathParam('boardId')[0];

	// the result from the JswBoardsRefactored query can sometimes be out of sync - eg when creating a new board
	// .: if the project is selected, and we are on board-scoped routes (ie boardIdPath is not undefined)
	// we check if the mostRecentlyViewedBoard matches the current selected path
	// if mostRecentlyViewedBoard does not match, we refetch boards
	useEffect(() => {
		const isMostRecentlyViewedBoardOutofSync =
			isProjectSelected &&
			boardIdPath &&
			mostRecentlyViewedBoardId &&
			!isSelectedPath(ENTITY_ID.BOARD(mostRecentlyViewedBoardId.toString()));

		if (expVal('blu-6805-fix-board-sync', 'isEnabled', false)) {
			if (isMostRecentlyViewedBoardOutofSync) {
				boardsSuspenselessRefetch({
					projectKey: project.projectKey,
					noOfBoardsToFetch: NO_OF_BOARDS_TO_FETCH,
					cloudId,
					entityAri,
					ownerAri,
				});
			} else if (isProjectSelected && boardIdPath && mostRecentlyViewedBoardId) {
				// Even if most recently viewed board is in sync, we need to check if boards are in the correct order. If not, we need to refetch.
				const isMostRecentlyViewedBoardFavourite = project.boards?.edges?.find(
					(edge) => edge?.node?.boardId === mostRecentlyViewedBoardId,
				)?.node?.favouriteValue?.isFavourite;
				const expectedBoardIndex = isMostRecentlyViewedBoardFavourite ? 0 : noOfFavouriteBoards;
				const isBoardInCorrectPosition =
					mostRecentlyViewedBoardId === project.boards?.edges?.[expectedBoardIndex]?.node?.boardId;

				if (!isBoardInCorrectPosition) {
					boardsSuspenselessRefetch({
						projectKey: project.projectKey,
						noOfBoardsToFetch: NO_OF_BOARDS_TO_FETCH,
						cloudId,
						entityAri,
						ownerAri,
					});
				}
			}
		}
	}, [
		boardsSuspenselessRefetch,
		project.id,
		project.projectKey,
		cloudId,
		entityAri,
		ownerAri,
		isProjectSelected,
		boardIdPath,
		mostRecentlyViewedBoardId,
		isSelectedPath,
		noOfFavouriteBoards,
		project.boards?.edges,
	]);

	const handleBoardClick = useCallback(
		(boardId: number) => () => {
			sendBoardNavigationItemInsideProjectsAnalyticsEvent({
				recent: !project.isFavourite,
				starred: project.isFavourite ?? false,
				projectId: project.projectId,
				boardId,
				projectType: project.projectType,
			});
		},
		[
			project.isFavourite,
			project.projectId,
			project.projectType,
			sendBoardNavigationItemInsideProjectsAnalyticsEvent,
		],
	);
	const handleAllBoardsClick = useCallback(
		() => () => {
			sendViewAllBoardsAnalyticsEvent({
				projectId: project.projectId,
				projectType: project.projectType,
			});
		},
		[project.projectId, project.projectType, sendViewAllBoardsAnalyticsEvent],
	);

	const { push } = fg('navx-1724-add-auto-select-behaviour')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useRouterActions()
		: // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			({ push: noop } as unknown as RouterActionsType);

	// Calculate favorite boards status using refactored utility functions
	const favoriteBoardsStatus = useMemo(() => {
		const allBoardEdges = convertGraphQLBoardEdges(project?.boards?.edges || []);
		const hasItems = hasFavoriteBoards(allBoardEdges);
		const hasNextPage = hasMoreFavoriteBoards(allBoardEdges, noOfBoardsToShow);

		// Create a set of favorite board IDs
		const idSet = new Set<string>();
		orderedFavoriteBoards.forEach((edge) => {
			if (edge?.node?.boardId) {
				idSet.add(edge.node.boardId.toString());
			}
		});

		return { hasItems, hasNextPage, idSet };
	}, [project?.boards?.edges, noOfBoardsToShow, orderedFavoriteBoards]);

	// FIX: Use orderedFavoriteBoards instead of boardsToShow for currentItems
	// This ensures drag-and-drop system uses the correctly ordered data
	const currentItems: Array<{ id: string; label: string }> =
		orderedFavoriteBoards?.map((item) => ({
			id: item?.node?.boardId?.toString() || '',
			label: item?.node?.name || '',
		})) || [];

	return (
		<>
			<StarredSectionWithFocusRestoration
				currentItems={currentItems}
				currentOrder={currentSidebarOrder}
				projectKey={project.projectKey}
				favoriteItems={{
					hasItems: favoriteBoardsStatus.hasItems,
					hasNextPage: favoriteBoardsStatus.hasNextPage,
					idSet: favoriteBoardsStatus.idSet,
					list:
						orderedFavoriteBoards.map((edge, index) => {
							const boardData = edge?.node && {
								id: edge.node.boardId?.toString() || '',
								name: edge.node.name || '',
								type: SCRUM, // Default type since we don't have boardType anymore
								favourite: edge.node.favouriteValue?.isFavourite || false,
								admins: { users: [], groups: [] },
								canEdit: true, // Default since we don't have canEdit anymore
								dataId: edge.node.id || '',
							};

							return (
								<BoardItem
									key={edge?.node?.id}
									edge={edge}
									index={index}
									projectKey={projectKey}
									project={project}
									isAnyBoardSelected={isAnyBoardSelected}
									isProjectScoped={isProjectScoped}
									mostRecentlyViewedBoardId={mostRecentlyViewedBoardId}
									isSelectedPath={isSelectedPath}
									isControlledPath={isControlledPath}
									handleBoardClick={handleBoardClick}
									routerPush={push}
									url={edge?.node?.defaultNavigationItem?.url}
									boardActions={
										<BoardActions
											board={boardData}
											projectFragment={project}
											placement="right"
											isParentProjectStarred={project?.isFavourite}
											isBoardFavorite={edge?.node?.favouriteValue?.isFavourite || false}
											iconButtonSpacing="compact"
										/>
									}
								/>
							);
						}) || [],
				}}
			/>
			<RenderRecentBoardsSection
				recentItems={{
					list:
						boardsToShow
							?.filter((edge) => !edge?.node?.favouriteValue?.isFavourite)
							.map((edge, index) => {
								const boardData = edge?.node && {
									id: edge.node.boardId?.toString() || '',
									name: edge.node.name || '',
									type: SCRUM, // Default type since we don't have boardType anymore
									favourite: edge.node.favouriteValue?.isFavourite || false,
									admins: { users: [], groups: [] },
									canEdit: true, // Default since we don't have canEdit anymore
									dataId: edge.node.id || '',
								};

								return (
									<BoardItem
										key={edge?.node?.id}
										edge={edge}
										index={index}
										projectKey={projectKey}
										project={project}
										isAnyBoardSelected={isAnyBoardSelected}
										isProjectScoped={isProjectScoped}
										mostRecentlyViewedBoardId={mostRecentlyViewedBoardId}
										isSelectedPath={isSelectedPath}
										isControlledPath={isControlledPath}
										handleBoardClick={handleBoardClick}
										routerPush={push}
										url={edge?.node?.defaultNavigationItem?.url}
										boardActions={
											<BoardActions
												board={boardData}
												projectFragment={project}
												placement="right"
												isParentProjectStarred={project?.isFavourite}
												isBoardFavorite={edge?.node?.favouriteValue?.isFavourite || false}
												iconButtonSpacing="compact"
											/>
										}
									/>
								);
							}) || [],
					hasItems: true,
				}}
			/>

			{showViewAllBoards && (
				<ViewAllEntityMenuItem
					onClick={handleAllBoardsClick}
					href={viewAllBoardsInProjectHref}
					menuId={MENU_ID_BOARDS_VIEW_ALL}
				>
					{formatMessage(messages.viewAllBoardsNonFinal)}
				</ViewAllEntityMenuItem>
			)}
		</>
	);
}

// Remove when navx-1724-add-auto-select-behaviour is cleaned up
const noop = () => {
	/* empty */
};
